import React, { useState, useEffect } from 'react';
import './App.css';
import Profile from './Profile';
import Privacy from './Privacy';
import About from './About';
import CommunityGuidelines from './CommunityGuidelines';

// API URL from environment variable, falls back to empty string for local dev with proxy
const API_URL = process.env.REACT_APP_API_URL || '';

// Predefined tags for gender critical topics
const PREDEFINED_TAGS = [
  'sports',
  'healthcare',
  'prisons',
  'legislation',
  'education',
  'safeguarding',
  'free speech',
  'feminism',
  'detransition',
  'gender ideology',
  'women\'s spaces',
  'lgb alliance',
  'parental rights',
  'social media',
  'activism'
];

function App() {
  const [posts, setPosts] = useState([]);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostType, setNewPostType] = useState('text');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostUrl, setNewPostUrl] = useState('');
  const [newPostTags, setNewPostTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [replyContent, setReplyContent] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [editPostTitle, setEditPostTitle] = useState('');
  const [editPostContent, setEditPostContent] = useState('');
  const [editPostUrl, setEditPostUrl] = useState('');
  const [sortBy, setSortBy] = useState('hot'); // 'hot', 'new', 'top'
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showPostForm, setShowPostForm] = useState(false);

  // Authentication state
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login', 'signup', 'forgot-password', 'reset-password'
  const [resetToken, setResetToken] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetDisplayName, setResetDisplayName] = useState('');
  const [authDisplayName, setAuthDisplayName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirmPassword, setAuthConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [biologicalWomanAcknowledged, setBiologicalWomanAcknowledged] = useState(false);

  // Profile state
  const [showProfile, setShowProfile] = useState(false);
  const [profileUsername, setProfileUsername] = useState('');

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  // Page modals state
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [aboutScrollTo, setAboutScrollTo] = useState(null);

  useEffect(() => {
    fetchPosts();
    fetchTags();
    checkAuth();
    handleEmailVerification();
  }, []);

  // Refetch posts when selected tags change
  useEffect(() => {
    fetchPosts(searchQuery, selectedTags);
  }, [selectedTags]);

  const handleEmailVerification = async () => {
    // Check if URL has verification token or reset token
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const resetTokenParam = urlParams.get('resetToken');

    if (resetTokenParam) {
      // Show reset password modal
      setResetToken(resetTokenParam);
      setAuthMode('reset-password');
      setShowAuthModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (token) {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`, {
          credentials: 'include'
        });

        const data = await response.json();

        if (response.ok) {
          // Email verified successfully, user is now logged in
          setUser(data.user);
          setAuthSuccess('Email verified successfully! Welcome to the forum.');
          // Remove token from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          // Show success message
          setTimeout(() => {
            alert('Email verified successfully! You are now logged in.');
          }, 500);
        } else {
          // Verification failed
          alert(data.error || 'Email verification failed. Please try again or contact support.');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        console.error('Email verification error:', error);
        alert('Failed to verify email. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  };

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (error) {
      // Not authenticated - this is expected for non-logged-in users
    }
  };

  const fetchPosts = async (search = '', tagFilters = []) => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) {
        params.append('search', search.trim());
      }
      if (tagFilters.length > 0) {
        tagFilters.forEach(tag => params.append('tags', tag));
      }

      const url = params.toString()
        ? `${API_URL}/api/posts?${params.toString()}`
        : `${API_URL}/api/posts`;
      const response = await fetch(url);
      const data = await response.json();

      // Handle both paginated and non-paginated responses
      const postsArray = Array.isArray(data) ? data : data.posts;

      setPosts(postsArray);

      // Track impressions for all posts (when they're loaded)
      // Use a small delay to avoid blocking the UI
      setTimeout(() => {
        postsArray.forEach(post => {
          trackPostView(post.id);
        });
      }, 100);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tags`);
      const tags = await response.json();
      setAllTags(tags);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPosts(searchQuery, selectedTags);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // If search is cleared, fetch all posts
    if (!value.trim()) {
      fetchPosts('');
    }
  };

  const trackPostView = async (postId) => {
    try {
      // Track view in background, don't wait for response
      fetch(`${API_URL}/api/posts/${postId}/view`, {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {
        // Silently fail - impressions are not critical
      });
    } catch (error) {
      // Silently fail
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      if (!authDisplayName.trim()) {
        setAuthError('Display name is required');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ displayName: authDisplayName.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        setAuthSuccess(data.message || 'If an account with that display name exists, a password reset link has been sent.');
        setAuthDisplayName('');
      } else {
        setAuthError(data.error || 'Failed to send password reset email');
      }
    } catch (error) {
      setAuthError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      if (!resetDisplayName.trim()) {
        setAuthError('Display name is required');
        setLoading(false);
        return;
      }
      if (!resetPassword.trim()) {
        setAuthError('Password is required');
        setLoading(false);
        return;
      }
      if (resetPassword !== resetConfirmPassword) {
        setAuthError('Passwords do not match');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          token: resetToken, 
          displayName: resetDisplayName.trim(),
          password: resetPassword 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setAuthSuccess(data.message || 'Password reset successfully! You can now login with your new password.');
        setResetPassword('');
        setResetConfirmPassword('');
        setResetDisplayName('');
        setResetToken(null);
        setTimeout(() => {
          setAuthMode('login');
          setAuthSuccess('');
        }, 3000);
      } else {
        setAuthError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      setAuthError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      // Login mode - validate display name
      if (authMode === 'login') {
        if (!authDisplayName.trim()) {
          setAuthError('Display name is required');
          setLoading(false);
          return;
        }
      }

      // Signup mode - validate additional fields
      if (authMode === 'signup') {
        if (!biologicalWomanAcknowledged) {
          setAuthError('You must acknowledge that you are a biological woman to create an account');
          setLoading(false);
          return;
        }
        if (!authDisplayName.trim()) {
          setAuthError('Display name is required');
          setLoading(false);
          return;
        }
        if (!authEmail.trim()) {
          setAuthError('Email is required');
          setLoading(false);
          return;
        }

        // Strong password validation
        if (authPassword.length < 12) {
          setAuthError('Password must be at least 12 characters long');
          setLoading(false);
          return;
        }
        if (!/[A-Z]/.test(authPassword)) {
          setAuthError('Password must contain at least one uppercase letter');
          setLoading(false);
          return;
        }
        if (!/[a-z]/.test(authPassword)) {
          setAuthError('Password must contain at least one lowercase letter');
          setLoading(false);
          return;
        }
        if (!/[0-9]/.test(authPassword)) {
          setAuthError('Password must contain at least one number');
          setLoading(false);
          return;
        }
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(authPassword)) {
          setAuthError('Password must contain at least one special character');
          setLoading(false);
          return;
        }

        if (authPassword !== authConfirmPassword) {
          setAuthError('Passwords do not match');
          setLoading(false);
          return;
        }
      }

      const endpoint = authMode === 'login' ? `${API_URL}/api/auth/login` : `${API_URL}/api/auth/signup`;
      // For login, only send displayName and password (no email field)
      const body = authMode === 'login'
        ? { displayName: authDisplayName.trim(), password: authPassword }
        : { displayName: authDisplayName.trim(), email: authEmail.trim(), password: authPassword };

      // Double-check: ensure login requests never include email
      if (authMode === 'login' && body.email !== undefined) {
        delete body.email;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        if (authMode === 'signup') {
          // Signup successful - show verification message
          setAuthSuccess(data.message || 'Account created! Please check your email to verify your account.');
          setAuthDisplayName('');
          setAuthEmail('');
          setAuthPassword('');
          setAuthConfirmPassword('');
          setBiologicalWomanAcknowledged(false);
          // Don't close modal - let user see the success message
        } else {
          // Login successful
          setUser(data.user);
          setShowAuthModal(false);
          setAuthDisplayName('');
          setAuthEmail('');
          setAuthPassword('');
          setAuthError('');
        }
      } else {
        // Handle rate limiting (429)
        if (response.status === 429) {
          // Use the error message from the server which includes the wait time
          const errorMsg = data.error || `Too many requests. Please wait ${data.retryAfter || 15} minutes before trying again.`;
          setAuthError(errorMsg);
        } else if (data.details && Array.isArray(data.details)) {
          // Handle validation errors with details
          const errorMessages = data.details.map(detail => detail.message).join('\n');
          setAuthError(errorMessages || data.error || 'Authentication failed');
        } else {
          // Show specific error messages
          let errorMessage = data.error || 'Authentication failed';
          
          // Handle email verification error
          if (data.emailNotVerified) {
            errorMessage = 'Please verify your email before logging in. Check your inbox for the verification link.';
          }
          
          console.error('Auth error:', response.status, data);
          setAuthError(errorMessage);
        }
      }
    } catch (error) {
      setAuthError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Tag management functions
  const addTag = (tag) => {
    const cleanTag = tag.trim().toLowerCase();
    if (cleanTag && !newPostTags.includes(cleanTag) && newPostTags.length < 5) {
      setNewPostTags([...newPostTags, cleanTag]);
      setNewTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setNewPostTags(newPostTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(newTagInput);
    }
  };

  const toggleTagFilter = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();

    if (!newPostTitle.trim()) {
      alert('Please enter a title');
      return;
    }
    if (newPostType === 'text' && !newPostContent.trim()) {
      alert('Please enter post content');
      return;
    }
    if ((newPostType === 'image' || newPostType === 'link') && !newPostUrl.trim()) {
      alert(newPostType === 'image' ? 'Please enter an image URL from Imgur' : 'Please enter a URL');
      return;
    }

    setLoading(true);
    try {
      const url = `${API_URL}/api/posts`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newPostTitle,
          type: newPostType,
          content: newPostType === 'text' ? newPostContent : '',
          url: (newPostType === 'image' || newPostType === 'link') ? newPostUrl : '',
          tags: newPostTags
        })
      });


      if (response.ok) {
        await response.json();
        setNewPostTitle('');
        setNewPostContent('');
        setNewPostUrl('');
        setNewPostType('text');
        setNewPostTags([]);
        setNewTagInput('');
        setShowPostForm(false); // Collapse form after successful submission
        fetchPosts();
        fetchTags(); // Refresh tags in case new ones were added
      } else if (response.status === 401) {
        console.error('❌ Not authenticated');
        alert('Please login to post');
      } else {
        const data = await response.json();
        console.error('❌ Post creation failed:', data);
        alert(data.error || 'Failed to create post');
      }
    } catch (error) {
      console.error('❌ Exception creating post:', error);
      alert('Failed to create post');
    } finally {
    setLoading(false);
    }
  };

  const handleReply = async (postId, parentId = null) => {
    const key = parentId ? `${postId}-${parentId}` : postId;
    const content = replyContent[key];
    if (!content || !content.trim()) return;

    setLoading(true);
    try {
      // Only include parentId in body if it's not null
      const requestBody = { content };
      if (parentId) {
        requestBody.parentId = parentId;
      }
      
      const response = await fetch(`${API_URL}/api/posts/${postId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setReplyContent({ ...replyContent, [key]: '' });
        fetchPosts();
      } else {
        const data = await response.json();
        if (response.status === 401) {
        alert('Please login to reply');
        } else {
          // Show detailed validation errors if available
          let errorMessage = data.error || 'Failed to create comment';
          if (data.details && Array.isArray(data.details) && data.details.length > 0) {
            errorMessage += ': ' + data.details.map(d => d.message).join(', ');
          }
          alert(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error creating reply:', error);
      alert('Failed to create comment. Please try again.');
    }
    setLoading(false);
  };

  const handleUpvotePost = async (postId) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/upvote`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error upvoting post:', error);
    }
  };

  const handleUpvoteReply = async (postId, replyId) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/replies/${replyId}/upvote`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error upvoting reply:', error);
    }
  };

  const handleReportComment = async (postId, replyId) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const reason = prompt('Please provide a reason for reporting this comment (optional):');
    if (reason === null) {
      return; // User cancelled
    }

    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/replies/${replyId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: reason || 'No reason provided' })
      });

      if (response.ok) {
        const data = await response.json();
        alert('Comment reported successfully. Thank you for helping keep our community safe.' + (data.isHidden ? ' The comment has been hidden and the user has been notified.' : ''));
        fetchPosts();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to report comment');
      }
    } catch (error) {
      console.error('Error reporting comment:', error);
      alert('Failed to report comment');
    }
  };

  const handleReportPost = async (postId) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const reason = prompt('Please provide a reason for reporting this post (optional):');
    if (reason === null) {
      return; // User cancelled
    }

    try {
      const response = await fetch(`${API_URL}/api/posts/${postId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: reason || 'No reason provided' })
      });

      if (response.ok) {
        alert('Post reported successfully. Thank you for helping keep our community safe.');
        fetchPosts();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to report post');
      }
    } catch (error) {
      console.error('Error reporting post:', error);
      alert('Failed to report post');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post.id);
    setEditPostTitle(post.title);
    setEditPostContent(post.type === 'text' ? post.content : '');
    setEditPostUrl((post.type === 'image' || post.type === 'link') ? post.url : '');
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
    setEditPostTitle('');
    setEditPostContent('');
    setEditPostUrl('');
  };

  const handleSaveEdit = async (postId) => {
    if (!editPostTitle.trim()) {
      alert('Please enter a title');
      return;
    }
    if (editingPost && posts.find(p => p.id === postId)?.type === 'text' && !editPostContent.trim()) {
      alert('Please enter post content');
      return;
    }
    const post = posts.find(p => p.id === postId);
    if (editingPost && (post?.type === 'image' || post?.type === 'link') && !editPostUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    setLoading(true);
    try {
      const post = posts.find(p => p.id === postId);
      const response = await fetch(`${API_URL}/api/posts/${postId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editPostTitle,
          content: post.type === 'text' ? editPostContent : '',
          url: (post.type === 'image' || post.type === 'link') ? editPostUrl : ''
        })
      });

      if (response.ok) {
        handleCancelEdit();
        fetchPosts();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update post');
      }
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Failed to update post');
    }
    setLoading(false);
  };

  const handleDeletePost = (postId) => {
    setPostToDelete(postId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;

    setLoading(true);
    setShowDeleteConfirm(false);
    try {
      const response = await fetch(`${API_URL}/api/posts/${postToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchPosts();
        setPostToDelete(null);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
    setLoading(false);
    setPostToDelete(null);
  };

  const cancelDeletePost = () => {
    setShowDeleteConfirm(false);
    setPostToDelete(null);
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Convert Imgur page URLs to direct image URLs
  const getImageUrl = (url) => {
    if (!url) return url;
    
    // If it's already a direct image URL (i.imgur.com or ends with image extension), return as is
    if (url.includes('i.imgur.com') || /\.(jpg|jpeg|png|gif|webp)$/i.test(url)) {
      return url;
    }
    
    // Convert Imgur page URLs to direct image URLs
    // imgur.com/xxxxx -> i.imgur.com/xxxxx.jpg
    // imgur.com/a/xxxxx -> keep as is (albums need special handling)
    const imgurPageMatch = url.match(/imgur\.com\/([a-zA-Z0-9]+)(?:\.[a-z]+)?$/);
    if (imgurPageMatch && !url.includes('/a/')) {
      const imageId = imgurPageMatch[1];
      return `https://i.imgur.com/${imageId}.jpg`;
    }
    
    // If it's an album or gallery, return original URL
    return url;
  };

  // Extract YouTube video ID and get thumbnail URL
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    
    // Match various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/.*[?&]v=([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }
    
    return null;
  };

  // Check if URL is a YouTube video
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return /youtube\.com|youtu\.be/.test(url);
  };

  // Check if URL is an image
  const isImageUrl = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || 
           url.includes('i.imgur.com') || 
           url.includes('imgur.com');
  };

  // Build threaded comment tree
  const buildCommentTree = (replies) => {
    if (!replies || replies.length === 0) {
      return [];
    }

    const replyMap = new Map();
    const rootReplies = [];

    // First pass: create map of all replies
    replies.forEach(reply => {
      if (reply && reply.id) {
        replyMap.set(reply.id, { 
          ...reply, 
          children: [],
          displayName: reply.displayName // Ensure displayName is preserved
        });
      }
    });

    // Second pass: build tree structure
    replies.forEach(reply => {
      if (reply && reply.id) {
        const replyNode = replyMap.get(reply.id);
        if (reply.parentId && replyMap.has(reply.parentId)) {
          replyMap.get(reply.parentId).children.push(replyNode);
        } else {
          rootReplies.push(replyNode);
        }
      }
    });

    // Sort by upvotes and timestamp
    const sortReplies = (replies) => {
      if (!replies || replies.length === 0) {
        return [];
      }
      return replies.sort((a, b) => {
        if (sortBy === 'top') {
          return (b.upvoteCount || 0) - (a.upvoteCount || 0);
        }
        return new Date(b.timestamp) - new Date(a.timestamp);
      }).map(reply => ({
        ...reply,
        children: sortReplies(reply.children)
      }));
    };

    return sortReplies(rootReplies);
  };

  // Sort posts
  const getSortedPosts = () => {
    const sorted = [...posts];
    if (sortBy === 'hot' || sortBy === 'top') {
      return sorted.sort((a, b) => (b.upvoteCount || 0) - (a.upvoteCount || 0));
    } else if (sortBy === 'new') {
      return sorted.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
    return sorted;
  };

  // Render nested comments
  // Helper function to linkify URLs in text
  const linkifyText = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="comment-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const renderComment = (comment, postId, depth = 0) => {
    const maxDepth = 5;
    const isCollapsed = expandedPosts[`comment-${comment.id}`] === false;
    const key = `${postId}-${comment.id}`;

    return (
      <div key={comment.id} className={`comment comment-depth-${Math.min(depth, maxDepth)}`}>
        <div className="comment-header">
          <button onClick={() => openProfile(comment.displayName)} className="author-link">
            {comment.displayName}
            {comment.verified && (
              <span className="verified-badge" title="Verified user">✓</span>
            )}
          </button>
          <span className="timestamp">{formatDate(comment.timestamp)}</span>
          {depth < maxDepth && (
            <button
              className="collapse-btn"
              onClick={() => setExpandedPosts({
                ...expandedPosts,
                [`comment-${comment.id}`]: !isCollapsed
              })}
            >
              {isCollapsed ? '[+]' : '[-]'}
            </button>
          )}
        </div>
        {!isCollapsed && (
          <>
            {comment.isHidden ? (
              <div className="comment-hidden">
                <p>This comment has been hidden due to multiple reports and is pending review.</p>
              </div>
            ) : (
              <>
                {editingComment === comment.id ? (
                  <div className="edit-comment-form">
                    <textarea
                      value={editCommentContent}
                      onChange={(e) => setEditCommentContent(e.target.value)}
                      placeholder="Edit your comment..."
                      rows="3"
                      disabled={loading}
                    />
                    <div className="edit-form-actions">
                      <button
                        onClick={async () => {
                          if (!editCommentContent.trim()) return;
                          setLoading(true);
                          try {
                            const response = await fetch(`${API_URL}/api/posts/${postId}/replies/${comment.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ content: editCommentContent })
                            });
                            if (response.ok) {
                              setEditingComment(null);
                              setEditCommentContent('');
                              fetchPosts();
                            } else {
                              const data = await response.json();
                              alert(data.error || 'Failed to update comment');
                            }
                          } catch (error) {
                            console.error('Error updating comment:', error);
                            alert('Failed to update comment');
                          }
                          setLoading(false);
                        }}
                        disabled={loading || !editCommentContent.trim()}
                      >
                        Save
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => {
                          setEditingComment(null);
                          setEditCommentContent('');
                        }}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="comment-content">{linkifyText(comment.content)}</div>
                    <div className="comment-actions">
                      <button
                        className={`upvote-btn upvote-btn-small ${comment.upvotes?.includes(user?.id) ? 'upvoted' : ''}`}
                        onClick={() => handleUpvoteReply(postId, comment.id)}
                        disabled={!user}
                      >
                        ▲ {comment.upvoteCount || 0}
                      </button>
                      {user && (
                        <>
                          {comment.displayName === user.displayName ? (
                            <>
                              <button
                                className="edit-btn"
                                onClick={() => {
                                  setEditingComment(comment.id);
                                  setEditCommentContent(comment.content);
                                }}
                                title="Edit your comment"
                              >
                                Edit
                              </button>
                              <button
                                className="delete-btn"
                                onClick={async () => {
                                  if (!window.confirm('Are you sure you want to delete this comment?')) return;
                                  setLoading(true);
                                  try {
                                    const response = await fetch(`${API_URL}/api/posts/${postId}/replies/${comment.id}`, {
                                      method: 'DELETE',
                                      credentials: 'include'
                                    });
                                    if (response.ok) {
                                      fetchPosts();
                                    } else {
                                      const data = await response.json();
                                      alert(data.error || 'Failed to delete comment');
                                    }
                                  } catch (error) {
                                    console.error('Error deleting comment:', error);
                                    alert('Failed to delete comment');
                                  }
                                  setLoading(false);
                                }}
                                title="Delete your comment"
                              >
                                Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="reply-btn"
                                onClick={() => {
                                  const newContent = { ...replyContent };
                                  newContent[key] = '';
                                  setReplyContent(newContent);
                                  // Scroll to reply form
                                  setTimeout(() => {
                                    document.querySelector(`.reply-form-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                  }, 100);
                                }}
                              >
                                Reply
                              </button>
                              <button
                                className="report-btn"
                                onClick={() => handleReportComment(postId, comment.id)}
                                title="Report this comment"
                              >
                                ⚠️ Report
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {user && replyContent[key] !== undefined && (
              <div className={`reply-form reply-form-${key}`}>
                <textarea
                  value={replyContent[key] || ''}
                  onChange={(e) => setReplyContent({ ...replyContent, [key]: e.target.value })}
                  placeholder="Write a reply..."
                  rows="2"
                  disabled={loading}
                />
                <div className="reply-form-actions">
                  <button
                    onClick={() => handleReply(postId, comment.id)}
                    disabled={loading || !replyContent[key]?.trim()}
                  >
                    Reply
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      const newContent = { ...replyContent };
                      delete newContent[key];
                      setReplyContent(newContent);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {comment.children && comment.children.length > 0 && (
              <div className="comment-children">
                {comment.children.map(child => renderComment(child, postId, depth + 1))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setAuthError('');
    setAuthSuccess('');
    // Clear all fields when opening modal
    setAuthDisplayName('');
    setAuthEmail('');
    setAuthPassword('');
    setAuthConfirmPassword('');
    setBiologicalWomanAcknowledged(false);
  };

  const openProfile = (identifier) => {
    // identifier is displayName

    if (!identifier || identifier === 'undefined') {
      console.error('❌ Cannot open profile: displayName is undefined or invalid');
      alert('Error: Cannot load profile - display name is missing');
      return;
    }

    setProfileUsername(identifier);
    setShowProfile(true);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <img src="/logo.png" alt="GC Women Logo" className="header-logo" />
            <h1>Gender Critical Women</h1>
          </div>
          <div className="auth-buttons">
            {user ? (
              <div className="user-info">
                <span className="username">
                  Welcome, <button onClick={() => openProfile(user.displayName)} className="username-link">{user.displayName}</button>
                </span>
                <button onClick={handleLogout} className="btn-logout">
                  Logout
                </button>
              </div>
            ) : (
              <>
                <button onClick={() => openAuthModal('login')} className="btn-auth">
                  Login
                </button>
                <button onClick={() => openAuthModal('signup')} className="btn-auth btn-primary">
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
        {!user && (
        <div className="intro">
          <p>
              Welcome to a platform made by and for biological women who hold <a href="#about" onClick={(e) => { e.preventDefault(); setAboutScrollTo('gender-critical'); setShowAbout(true); }} className="gender-critical-link">gender critical</a> beliefs.
            This is an anonymous forum where women can freely discuss sex-based rights,
              gender ideology, and the erosion of women's spaces without fear of censorship. Please check out the <a href="#guidelines" onClick={(e) => { e.preventDefault(); setShowGuidelines(true); }} className="guidelines-link">community guidelines</a> and <a href="#privacy" onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }} className="privacy-link">privacy policy</a>.
          </p>
          <p className="privacy-note">
            Accounts are free to create. Basic accounts may post and upvote. Accounts which are fully verified are invited to a private 
            discord server and whatsapp group. Privacy and anonymity are fully protected.
          </p>
        </div>
        )}
      </header>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAuthModal(false)}>
              ×
            </button>
            <h2>
              {authMode === 'login' ? 'Login' : 
               authMode === 'signup' ? 'Create Account' :
               authMode === 'forgot-password' ? 'Reset Password' :
               'Set New Password'}
            </h2>
            {authSuccess ? (
              <div className="success-message-container">
                <div className="success-message">{authSuccess}</div>
                {authMode === 'signup' && (
                <p className="email-instruction">Please check your email inbox and click the verification link to complete your registration.</p>
                )}
                <button onClick={() => { 
                  setShowAuthModal(false); 
                  setAuthSuccess(''); 
                  if (authMode === 'reset-password') {
                    setAuthMode('login');
                    setResetToken(null);
                  }
                }} className="btn-submit">
                  Close
                </button>
              </div>
            ) : authMode === 'forgot-password' ? (
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={authDisplayName}
                    onChange={(e) => setAuthDisplayName(e.target.value)}
                    placeholder="Enter your display name"
                    required
                  />
                </div>
                {authError && <div className="error-message">{authError}</div>}
                <button type="submit" disabled={loading} className="btn-submit">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            ) : authMode === 'reset-password' ? (
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={resetDisplayName}
                    onChange={(e) => setResetDisplayName(e.target.value)}
                    placeholder="Enter your display name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Enter new password (min 12 chars, must include uppercase, lowercase, number, special)"
                    required
                    minLength="12"
                  />
                  <div className="password-requirements">
                    <small>Password must be at least 12 characters and include:</small>
                    <ul>
                      <li>One uppercase letter</li>
                      <li>One lowercase letter</li>
                      <li>One number</li>
                      <li>One special character</li>
                    </ul>
                  </div>
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    required
                    minLength="12"
                  />
                </div>
                {authError && <div className="error-message">{authError}</div>}
                <button type="submit" disabled={loading} className="btn-submit">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleAuth}>
                {authMode === 'login' ? (
                  <>
                    <div className="form-group">
                      <label>Display Name</label>
                      <input
                        type="text"
                        value={authDisplayName}
                        onChange={(e) => setAuthDisplayName(e.target.value)}
                        placeholder="Enter your display name"
                        required
                        minLength="2"
                        maxLength="50"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="biological-woman-acknowledgment">
                      <div className="acknowledgment-content">
                        <p className="acknowledgment-text">
                          <strong>Important: </strong> 
                          By creating an account, you acknowledge that you were born biologically female.
                        </p>
                        <label className="acknowledgment-checkbox">
                          <input
                            type="checkbox"
                            checked={biologicalWomanAcknowledged}
                            onChange={(e) => setBiologicalWomanAcknowledged(e.target.checked)}
                            required
                          />
                          <span>I am a biological woman</span>
                        </label>
                      </div>
                    </div>
                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      value={authDisplayName}
                      onChange={(e) => setAuthDisplayName(e.target.value)}
                      placeholder="How you'll appear on posts"
                      required
                      minLength="2"
                      maxLength="50"
                    />
                      <div className="display-name-notice">
                        <small>
                          <strong>Note:</strong> You will use this display name (not your email) to log in. 
                          This is for security purposes. Please make note of your display name.
                        </small>
                  </div>
                    </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                        placeholder="Enter your email address"
                    required
                  />
                </div>
                  </>
                )}
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    placeholder="Enter password (min 12 chars, must include uppercase, lowercase, number, special)"
                    required
                    minLength="12"
                  />
                  {authMode === 'signup' && (
                    <div className="password-requirements">
                      <small>Password must be at least 12 characters and include:</small>
                      <ul>
                        <li>One uppercase letter</li>
                        <li>One lowercase letter</li>
                        <li>One number</li>
                        <li>One special character</li>
                      </ul>
                    </div>
                  )}
                </div>
                {authMode === 'signup' && (
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      value={authConfirmPassword}
                      onChange={(e) => setAuthConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      required
                      minLength="12"
                    />
                  </div>
                )}
                {authError && <div className="error-message">{authError}</div>}
                <button type="submit" disabled={loading} className="btn-submit">
                  {loading ? 'Please wait...' : authMode === 'login' ? 'Login' : 'Sign Up'}
                </button>
              </form>
            )}
            <p className="auth-switch">
              {authMode === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <button onClick={() => setAuthMode('signup')} className="link-button">
                    Sign up
                  </button>
                  {' • '}
                  <button onClick={() => setAuthMode('forgot-password')} className="link-button">
                    Forgot password?
                  </button>
                </>
              ) : authMode === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <button onClick={() => setAuthMode('login')} className="link-button">
                    Login
                  </button>
                </>
              ) : authMode === 'forgot-password' ? (
                <>
                  Remember your password?{' '}
                  <button onClick={() => { setAuthMode('login'); setAuthEmail(''); setAuthError(''); }} className="link-button">
                    Back to login
                  </button>
                </>
              ) : null}
            </p>
          </div>
        </div>
      )}

      <div className="container">
        {/* <section className="heatmap-section">
          <h2>Global Community Map</h2>
          <p className="map-description">
            This heatmap shows the anonymized geographic distribution of all posts made on this forum.
          </p>
          <div className="map-container">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: '400px', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {locations.map((loc, index) => (
                <CircleMarker
                  key={index}
                  center={[loc.lat, loc.lng]}
                  radius={6}
                  fillColor="#d4b3e8"
                  fillOpacity={0.6}
                  stroke={true}
                  color="#c9a3d9"
                  weight={1}
                >
                  <Popup>
                    <div>
                      <strong>Post Location</strong><br />
                      Country: {loc.country || 'Unknown'}<br />
                      {loc.timestamp && `Time: ${formatDate(loc.timestamp)}`}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </section> */}

        <section className="forum-section">
          {/* Tag Filter Bar */}
          {allTags.length > 0 && (
            <div className="tag-filter-bar">
              <div className="tag-filter-scroll">
                <button
                  className={`filter-tag ${selectedTags.length === 0 ? 'active' : ''}`}
                  onClick={() => setSelectedTags([])}
                >
                  All
                </button>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    className={`filter-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTagFilter(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <select
                className="tag-filter-dropdown"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    toggleTagFilter(e.target.value);
                  }
                }}
              >
                <option value="">Select tag...</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="forum-header">
            <form className="search-form" onSubmit={handleSearch}>
              <input
                type="text"
                className="search-input"
                placeholder="Search posts by title, content, or author..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <button type="submit" className="search-btn">
                🔍
              </button>
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchQuery('');
                    fetchPosts('');
                  }}
                >
                  ✕
                </button>
              )}
            </form>
            <div className="sort-controls">
              <button
                className={`sort-btn ${sortBy === 'hot' ? 'active' : ''}`}
                onClick={() => setSortBy('hot')}
              >
                Hot
              </button>
              <button
                className={`sort-btn ${sortBy === 'new' ? 'active' : ''}`}
                onClick={() => setSortBy('new')}
              >
                New
              </button>
              <button
                className={`sort-btn ${sortBy === 'top' ? 'active' : ''}`}
                onClick={() => setSortBy('top')}
              >
                Top
              </button>
            </div>
          </div>

          {searchQuery && (
            <div className="search-results-info">
              <p>Found {posts.length} {posts.length === 1 ? 'post' : 'posts'} matching "{searchQuery}"</p>
            </div>
          )}

          {user ? (
            <div className="post-form-container">
              {!showPostForm ? (
                <button 
                  type="button"
                  onClick={() => setShowPostForm(true)}
                  className="expand-post-form-btn"
                >
                  <span>✏️</span>
                  <span>Create New Post</span>
                </button>
              ) : (
            <div className="post-form">
                  <div className="post-form-header">
              <h3>Create New Post</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPostForm(false);
                        // Reset form when closing
                        setNewPostTitle('');
                        setNewPostContent('');
                        setNewPostUrl('');
                        setNewPostType('text');
                        setNewPostTags([]);
                        setNewTagInput('');
                      }}
                      className="collapse-post-form-btn"
                      aria-label="Close form"
                    >
                      ×
                    </button>
                  </div>
              <form onSubmit={handleCreatePost}>
                <input
                  type="text"
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                  placeholder="Post Title"
                  className="post-title-input"
                  disabled={loading}
                  required
                />
                <div className="post-type-selector">
                  <label>
                    <input
                      type="radio"
                      value="text"
                      checked={newPostType === 'text'}
                      onChange={(e) => setNewPostType(e.target.value)}
                      disabled={loading}
                    />
                    Text Post
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="image"
                      checked={newPostType === 'image'}
                      onChange={(e) => setNewPostType(e.target.value)}
                      disabled={loading}
                    />
                    Image Post
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="link"
                      checked={newPostType === 'link'}
                      onChange={(e) => setNewPostType(e.target.value)}
                      disabled={loading}
                    />
                    Link Post
                  </label>
                </div>
                {newPostType === 'text' ? (
                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder="Post content..."
                    rows="6"
                  disabled={loading}
                    required
                  />
                ) : newPostType === 'image' ? (
                  <div className="image-post-section">
                    <div className="imgur-instructions">
                      <div className="imgur-header">
                        <span className="imgur-icon">📷</span>
                        <span className="imgur-text">
                          <strong>Upload your image to Imgur first</strong>
                        </span>
                      </div>
                      <p className="imgur-help-text">
                        To share images, upload them to Imgur and paste the image URL here.
                      </p>
                      <a
                        href="https://imgur.com/upload"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="imgur-link-btn"
                      >
                        Open Imgur Upload →
                      </a>
                    </div>
                    <input
                      type="url"
                      value={newPostUrl}
                      onChange={(e) => setNewPostUrl(e.target.value)}
                      placeholder="Paste Imgur image URL here (e.g., https://i.imgur.com/xxxxx.jpg)"
                      className="post-url-input"
                      disabled={loading}
                      required
                    />
                    <small className="url-hint">
                      After uploading to Imgur, right-click the image and select "Copy image address"
                    </small>
                  </div>
                ) : (
                  <div className="link-post-section">
                    <input
                      type="url"
                      value={newPostUrl}
                      onChange={(e) => setNewPostUrl(e.target.value)}
                      placeholder="Paste URL here (e.g., https://example.com/article)"
                      className="post-url-input"
                      disabled={loading}
                      required
                    />
                    <small className="url-hint">
                      Share a link to an article, website, or other content
                    </small>
                  </div>
                )}

                {/* Tag selection */}
                <div className="tag-selection">
                  <label className="tag-label">Tags (optional, max 5)</label>
                  <div className="tag-input-container">
                    <input
                      type="text"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      placeholder="Type a tag and press Enter"
                      className="tag-input"
                      disabled={loading || newPostTags.length >= 5}
                    />
                    {newTagInput && (
                      <button
                        type="button"
                        onClick={() => addTag(newTagInput)}
                        className="tag-add-btn"
                        disabled={newPostTags.length >= 5}
                      >
                        Add
                      </button>
                    )}
                  </div>

                  {/* Predefined tags */}
                  <div className="predefined-tags">
                    {PREDEFINED_TAGS.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        className={`predefined-tag ${newPostTags.includes(tag) ? 'selected' : ''}`}
                        disabled={newPostTags.includes(tag) || newPostTags.length >= 5}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>

                  {/* Selected tags */}
                  {newPostTags.length > 0 && (
                    <div className="selected-tags">
                      {newPostTags.map(tag => (
                        <span key={tag} className="tag-chip">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="tag-remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading || !newPostTitle.trim() || (newPostType === 'text' && !newPostContent.trim()) || ((newPostType === 'image' || newPostType === 'link') && !newPostUrl.trim())}>
                  {loading ? 'Posting...' : 'Submit'}
                </button>
              </form>
                </div>
              )}
            </div>
          ) : (
            <div className="login-prompt">
              <p>
                <button onClick={() => openAuthModal('login')} className="link-button">
                  Login
                </button>
                {' '}or{' '}
                <button onClick={() => openAuthModal('signup')} className="link-button">
                  create an account
                </button>
                {' '}to post and upvote.
              </p>
            </div>
          )}

          <div className="posts-list">
            <h3>{getSortedPosts().length} {getSortedPosts().length === 1 ? 'Post' : 'Posts'}</h3>
            {getSortedPosts().length === 0 ? (
              <p className="no-posts">No posts yet. Be the first to share your thoughts!</p>
            ) : (
              getSortedPosts().map((post) => {
                const commentTree = buildCommentTree(post.replies || []);
                const totalComments = (post.replies || []).length;
                // Posts are expanded by default (show comments), can be collapsed by user
                const isExpanded = expandedPosts[post.id] !== false;

                return (
                  <div key={post.id} className="reddit-post">
                    <div className="post-voting">
                    <button
                        className={`vote-btn upvote ${post.upvotes?.includes(user?.id) ? 'active' : ''}`}
                      onClick={() => handleUpvotePost(post.id)}
                      disabled={!user}
                        title="Upvote"
                    >
                        ▲
                    </button>
                      <div className="vote-count">{post.upvoteCount || 0}</div>
                  </div>
                    <div className="post-content-wrapper">
                      <div className="post-header">
                        <button onClick={() => openProfile(post.displayName)} className="author-link">
                          {post.displayName}
                          {post.verified && (
                            <span className="verified-badge" title="Verified user">✓</span>
                          )}
                            </button>
                        <span className="post-meta">
                          <span className="timestamp">{formatDate(post.timestamp)}</span>
                          {post.type === 'image' && <span className="post-type-badge">Image</span>}
                          {post.type === 'link' && <span className="post-type-badge">Link</span>}
                          <span className="view-count">{post.viewCount || 0} views</span>
                          {user && (
                            <>
                              {user.displayName === post.displayName ? (
                                <span className="post-owner-actions">
                                  <button
                                    className="edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditPost(post);
                                    }}
                                    disabled={loading}
                                    title="Edit post"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePost(post.id);
                                    }}
                                    disabled={loading}
                                    title="Delete post"
                                  >
                                    Delete
                                  </button>
                                </span>
                              ) : (
                                <span className="post-actions-other">
                                  <button
                                    className="report-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReportPost(post.id);
                                    }}
                                    disabled={loading}
                                    title="Report this post"
                                  >
                                    ⚠️ Report
                                  </button>
                                </span>
                              )}
                            </>
                          )}
                        </span>
                          </div>
                      {editingPost === post.id ? (
                        <div className="edit-post-form">
                          <input
                            type="text"
                            value={editPostTitle}
                            onChange={(e) => setEditPostTitle(e.target.value)}
                            className="post-title-input"
                            disabled={loading}
                            required
                          />
                          {post.type === 'text' ? (
                            <textarea
                              value={editPostContent}
                              onChange={(e) => setEditPostContent(e.target.value)}
                              rows="6"
                              disabled={loading}
                              required
                            />
                          ) : post.type === 'image' ? (
                            <div className="image-post-section">
                              <div className="imgur-instructions">
                                <div className="imgur-header">
                                  <span className="imgur-icon">📷</span>
                                  <span className="imgur-text">
                                    <strong>Upload your image to Imgur first</strong>
                                  </span>
                                </div>
                                <p className="imgur-help-text">
                                  To share images, upload them to Imgur and paste the image URL here.
                                </p>
                                <a
                                  href="https://imgur.com/upload"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="imgur-link-btn"
                                >
                                  Open Imgur Upload →
                                </a>
                              </div>
                              <input
                                type="url"
                                value={editPostUrl}
                                onChange={(e) => setEditPostUrl(e.target.value)}
                                placeholder="Paste Imgur image URL here (e.g., https://i.imgur.com/xxxxx.jpg)"
                                className="post-url-input"
                                disabled={loading}
                                required
                              />
                              <small className="url-hint">
                                After uploading to Imgur, right-click the image and select "Copy image address"
                              </small>
                            </div>
                          ) : (
                            <div className="link-post-section">
                              <input
                                type="url"
                                value={editPostUrl}
                                onChange={(e) => setEditPostUrl(e.target.value)}
                                placeholder="Paste URL here (e.g., https://example.com/article)"
                                className="post-url-input"
                                disabled={loading}
                                required
                              />
                              <small className="url-hint">
                                Share a link to an article, website, or other content
                              </small>
                            </div>
                          )}
                          <div className="edit-form-actions">
                          <button
                              onClick={() => handleSaveEdit(post.id)}
                              disabled={loading || !editPostTitle.trim() || (post.type === 'text' && !editPostContent.trim()) || ((post.type === 'image' || post.type === 'link') && !editPostUrl.trim())}
                            >
                              {loading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={handleCancelEdit}
                              disabled={loading}
                            >
                              Cancel
                          </button>
                        </div>
                        </div>
                      ) : (
                        <>
                      <h3 className="post-title">
                        {(post.type === 'image' || post.type === 'link') && post.url ? (
                          <a href={post.url} target="_blank" rel="noopener noreferrer">
                            {post.title}
                          </a>
                        ) : (
                          post.title
                        )}
                      </h3>
                      {/* Display post tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="post-tags">
                          {post.tags.map(tag => (
                            <span
                              key={tag}
                              className="post-tag"
                              onClick={() => toggleTagFilter(tag)}
                              title="Click to filter by this tag"
                            >
                              {tag}
                            </span>
                      ))}
                    </div>
                  )}
                      {post.type === 'text' && post.content && (
                        <div className="post-content">{post.content}</div>
                      )}
                      {post.type === 'image' && post.url && (
                        <div className="post-image-container">
                          <a href={post.url} target="_blank" rel="noopener noreferrer">
                            <img 
                              src={getImageUrl(post.url)} 
                              alt={post.title}
                              className="post-image"
                              onError={(e) => {
                                // If image fails to load, show the URL as fallback
                                e.target.style.display = 'none';
                                const fallback = e.target.parentElement;
                                if (fallback && !fallback.querySelector('.image-fallback')) {
                                  const link = document.createElement('a');
                                  link.href = post.url;
                                  link.target = '_blank';
                                  link.rel = 'noopener noreferrer';
                                  link.className = 'external-link image-fallback';
                                  link.textContent = post.url;
                                  fallback.appendChild(link);
                                }
                              }}
                            />
                          </a>
                        </div>
                      )}
                      {post.type === 'link' && post.url && (
                        <div className="post-link">
                          {isYouTubeUrl(post.url) ? (
                            <a href={post.url} target="_blank" rel="noopener noreferrer" className="youtube-thumbnail-link">
                              <div className="youtube-thumbnail-container">
                                <img 
                                  src={getYouTubeThumbnail(post.url)} 
                                  alt={post.title}
                                  className="youtube-thumbnail"
                                  onError={(e) => {
                                    // If thumbnail fails to load, show the URL as fallback
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement;
                                    if (fallback && !fallback.querySelector('.link-fallback')) {
                                      const link = document.createElement('a');
                                      link.href = post.url;
                                      link.target = '_blank';
                                      link.rel = 'noopener noreferrer';
                                      link.className = 'external-link link-fallback';
                                      link.textContent = post.url;
                                      fallback.appendChild(link);
                                    }
                                  }}
                                />
                                <div className="youtube-play-overlay">
                                  <svg width="68" height="48" viewBox="0 0 68 48">
                                    <path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.63-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#f00"></path>
                                    <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                  </svg>
                                </div>
                              </div>
                            </a>
                          ) : isImageUrl(post.url) ? (
                            <div className="post-image-container">
                              <a href={post.url} target="_blank" rel="noopener noreferrer">
                                <img 
                                  src={getImageUrl(post.url)} 
                                  alt={post.title}
                                  className="post-image"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement;
                                    if (fallback && !fallback.querySelector('.image-fallback')) {
                                      const link = document.createElement('a');
                                      link.href = post.url;
                                      link.target = '_blank';
                                      link.rel = 'noopener noreferrer';
                                      link.className = 'external-link image-fallback';
                                      link.textContent = post.url;
                                      fallback.appendChild(link);
                                    }
                                  }}
                                />
                              </a>
                            </div>
                          ) : (
                            <a href={post.url} target="_blank" rel="noopener noreferrer" className="external-link">
                              {post.url}
                            </a>
                          )}
                        </div>
                      )}
                        </>
                      )}
                      <div className="post-actions">
                        <button
                          className={`action-btn comment-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => {
                            const newExpanded = !isExpanded;
                            setExpandedPosts({
                              ...expandedPosts,
                              [post.id]: newExpanded
                            });
                            // If expanding and user is logged in, focus the reply form
                            if (newExpanded && user) {
                              setTimeout(() => {
                                document.querySelector(`.reply-form-${post.id} textarea`)?.focus();
                              }, 100);
                            }
                          }}
                        >
                          {isExpanded ? 'Hide' : 'View'} {totalComments} {totalComments === 1 ? 'Comment' : 'Comments'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="comments-section">
                          {commentTree.length > 0 && (
                            <div className="comments">
                              {commentTree.map(comment => renderComment(comment, post.id, 0))}
                            </div>
                          )}
                  {user && (
                            <div className={`reply-form reply-form-${post.id}`}>
                      <textarea
                        value={replyContent[post.id] || ''}
                        onChange={(e) => setReplyContent({ ...replyContent, [post.id]: e.target.value })}
                                placeholder="What are your thoughts?"
                                rows="4"
                        disabled={loading}
                      />
                              <div className="reply-form-actions">
                      <button
                        onClick={() => handleReply(post.id)}
                        disabled={loading || !replyContent[post.id]?.trim()}
                      >
                                  Comment
                      </button>
                                {replyContent[post.id] && (
                                  <button
                                    className="cancel-btn"
                                    onClick={() => {
                                      const newContent = { ...replyContent };
                                      delete newContent[post.id];
                                      setReplyContent(newContent);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                    </div>
                  )}
                </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>

      {/* Profile Modal */}
      {showProfile && (
        <Profile
          username={profileUsername}
          currentUser={user}
          onClose={() => setShowProfile(false)}
          onLogout={handleLogout}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={cancelDeletePost}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Post</h2>
            <p>Are you sure you want to delete this post? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={cancelDeletePost}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={confirmDeletePost}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacy && (
        <Privacy onClose={() => setShowPrivacy(false)} />
      )}

      {/* Community Guidelines Modal */}
      {showGuidelines && (
        <CommunityGuidelines onClose={() => setShowGuidelines(false)} />
      )}

      {/* About Modal */}
      {showAbout && (
        <About onClose={() => { setShowAbout(false); setAboutScrollTo(null); }} scrollToSection={aboutScrollTo} />
      )}

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-links">
            <button onClick={() => setShowAbout(true)} className="footer-link">
              About
            </button>
            <span className="footer-separator">|</span>
            <button onClick={() => setShowGuidelines(true)} className="footer-link">
              Community Guidelines
            </button>
            <span className="footer-separator">|</span>
            <button onClick={() => setShowPrivacy(true)} className="footer-link">
              Privacy
            </button>
            <span className="footer-separator">|</span>
            <a href="mailto:gcwomensite@gmail.com" className="footer-link">
              Contact
            </a>
          </div>
          <p className="footer-copyright">
            © {new Date().getFullYear()} Gender Critical Women. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
